import Head from "next/head"
import {GetStaticProps, GetStaticPaths, NextPage} from "next"
import {getAllTripPaths, getTripData, getVisaReqSourceForCountrySlug} from "../../../src/data/content";
import {MainLayout} from "../../../src/components/main-layout";
import {MDXRemote, MDXRemoteSerializeResult} from "next-mdx-remote";
import {Title} from "../../../src/components/remark/title";
import {Intro} from "../../../src/components/remark/intro";
import {OfferLink} from "../../../src/components/remark/offer-link";
import {Youtube} from "../../../src/components/remark/youtube";
import {Img} from "../../../src/components/remark/img";
import {HiddenMarkup} from "../../../src/components/remark/hidden-markup";
import {Temp} from "../../../src/components/remark/temp";
import {VisaRequirements} from "../../../src/components/remark/visa-requirements";
import {Meters} from "../../../src/components/remark/meters";
import {Card} from "../../../src/components/remark/card";
import {Forex} from "../../../src/components/remark/forex";
import {PriceTable} from "../../../src/components/remark/price-table";
import getConfig from "next/config"

import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import {TemperaturesChart} from "../../../src/components/remark/temperatures-chart";
import {Grid, Container, Paper, Box, Typography, Hidden} from "@mui/material";

import {Gallery} from "../../../src/components/trip/gallery";
import {DetailPanel} from "../../../src/components/trip/detail-panel";
import {TripDetailHeading} from "../../../src/components/remark/headings/trip-detail-heading";
import {wrapper, useSelector, useDispatch} from "../../../src/store";
import {setSelectedTripId} from "../../../src/slices/user-session-slice";
import fs from "fs";
import {getTripsPath} from "seacrush-core"
import {A} from "../../../src/components/remark/a";
import {ProductJsonLd, ProductJsonLdProps, NextSeo} from "next-seo";
import {imageKitBase} from "../../../src/lib/imagekit-loader";
import {fetchReviewsByTag} from "../../../src/slices/reviews-slice";
import {ReviewList} from "../../../src/components/reviews/review-list";
import React, {useEffect} from "react";
import {TripDataProvider} from "../../../src/contexts/trip-context";
import {fetchInterestedDivers} from "../../../src/slices/trips-slice";
import TripDetailActions from "../../../src/components/trip/trip-detail-actions";
import {useTranslation} from "next-i18next";
import {useRouter} from "next-translate-routes/router";
import {fileUrlToUrl} from "next-translate-routes";

import {TripDb} from "seacrush-core"
import _tripDatabase from "../../../src/generated/trip-database.json";
import {WhatsApp} from "../../../src/components/whatsapp";

const tripDatabase = _tripDatabase as TripDb

interface TripProps {
    tripId: string
    offerLowUSD: number,
    offerHighUSD: number,
    markdownData: {
        frontmatter: any;
        subDir: string;
        source: MDXRemoteSerializeResult;
    }
    tripImageNames: string[];
    visaReqSource: MDXRemoteSerializeResult
}


const initialComponents = {
    Title,
    Intro,
    OfferLink,
    Youtube,
    Overview: HiddenMarkup,
    Temp,
    Meters,
    Card,
    Forex,
    PriceTable,
    h2: TripDetailHeading,
    a: A
}


const TripPage: NextPage<TripProps> = ({
                                           tripId,
                                           offerLowUSD,
                                           offerHighUSD,
                                           tripImageNames,
                                           visaReqSource,
                                           markdownData: {
                                               frontmatter,
                                               subDir,
                                               source
                                           }
                                       }) => {

    const {title, regionName, countryName, diveType, diveOperatorName, locationName, description} = frontmatter

    const {i18n} = useTranslation()

    const {reviews, aggregateRating} = useSelector((state) => {
        const {selectedAggregateRating, selectedTripReviews} = state.reviews
        return {
            reviews: selectedTripReviews,
            aggregateRating: selectedAggregateRating
        }
    });

    const router = useRouter()

    const {pathname, locales, locale, query} = router


    const components = {
        VisaRequirements: (props: any) => {
            // console.log(props)
            return <VisaRequirements
                source={visaReqSource}/>
        },
        img: (props: any) => {
            // console.log(props)
            return <Img
                {...props}
                src={`/content/trips/${tripId}/${props.src}`}/>
        },
        TemperaturesChart: (props: any) => <TemperaturesChart tripId={tripId}/>,
        ...initialComponents
    }

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(setSelectedTripId(tripId))
        dispatch(fetchReviewsByTag(tripId));
        dispatch(fetchInterestedDivers([tripId]))
    }, [])

    //somehow should get reviews and offers...
    const productData: ProductJsonLdProps = {
        productName: frontmatter["title"],
        description: frontmatter["description"],
        images: [
            `${imageKitBase}/content/trips/${tripId}/img/${tripImageNames[0]}`
        ],
        aggregateOffer: {
            highPrice: offerHighUSD,
            lowPrice: offerLowUSD,
            priceCurrency: "USD"
        }
    }

    if (aggregateRating) {
        productData.aggregateRating = {
            bestRating: "10",
            worstRating: "1",
            ratingValue: aggregateRating.ratingValue.toString(),
            reviewCount: aggregateRating.reviewCount.toString()
        }
    }

    return (
        <TripDataProvider tripPageData={{diveOperatorName, locationName, tripId}}>
            <div>
                <Head>
                    <title>{title + " | SeaCrush"}</title>
                    <meta name="description" content={frontmatter.description}/>
                </Head>

                <NextSeo
                    facebook={{
                        appId: process.env.NEXT_PUBLIC_FACEBOOK_APP_ID as string
                    }}
                    openGraph={{
                        url: `https://${process.env.NEXT_PUBLIC_SEACRUSH_HOST}${fileUrlToUrl({
                            pathname: router.pathname,
                            query: router.query
                        }, router.locale || "en")}`,
                        type: "website",
                        title: title,
                        description: description,
                        images: [
                            {
                                width: 640,
                                height: 426,
                                type: "image/webp",
                                url: `${process.env.NEXT_PUBLIC_IMAGE_ROOT}/content/trips/${tripId}/img/${tripImageNames[0].replace(".webp", `_640__.webp`)}`
                            },
                            {
                                width: 1920,
                                height: 1280,
                                type: "image/webp",
                                url: `${process.env.NEXT_PUBLIC_IMAGE_ROOT}/content/trips/${tripId}/img/${tripImageNames[0].replace(".webp", `_1920__.webp`)}`
                            }
                        ]
                    }}
                    twitter={{
                        cardType: "summary_large_image"
                    }}
                    languageAlternates={
                        locales?.filter((l) => l !== locale && l !== "default").map(
                            (l) => {
                                return {
                                    hrefLang: l,
                                    href: `https://${process.env.NEXT_PUBLIC_SEACRUSH_HOST}` + fileUrlToUrl({
                                            pathname,
                                            query: tripDatabase[tripId].slugs[l]
                                        },
                                        l)
                                }
                            }
                        )
                    }
                />
                <ProductJsonLd {...productData} />


                <section>

                    <Container maxWidth={"lg"}
                               sx={{
                                   minHeight: 300,
                                   mt: {
                                       xs: 3,
                                       sm: 0
                                   },
                                   px: {
                                       xs: 0,
                                       sm: 1
                                   },
                                   pb: 2
                               }}>


                        <span id={"photo-gallery"}></span>
                        <a href={`#photo-gallery`}/>

                        <Typography variant={"h3"} component={"h1"} sx={{p: 1}}>{title}</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={8}>
                                <Paper elevation={5} square>
                                    <Gallery imageNames={tripImageNames} tripId={tripId}/>
                                    <Box sx={{
                                        position: "relative",
                                        py: 1,
                                        px: {
                                            xs: 2,
                                            md: 3
                                        }
                                    }}>
                                        <ReviewList/>
                                        <div>
                                            <MDXRemote {...source} components={components}/>
                                        </div>
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>

                                <Hidden only={"xs"}>
                                    <DetailPanel tripId={tripId} title={title} diveType={diveType}
                                                 regionName={regionName} countryName={countryName}
                                                 featuredMedia={tripImageNames[0]}/>
                                </Hidden>


                            </Grid>

                        </Grid>
                    </Container>
                    <Hidden smUp>
                        <TripDetailActions tripId={tripId} title={title}/>
                    </Hidden>

                        <WhatsApp/>

                </section>
            </div>
        </TripDataProvider>
    )
}


TripPage.getLayout = (page) => (
    <MainLayout>
        {page}
    </MainLayout>
);

export default TripPage

export const getStaticPaths: GetStaticPaths = async (context) => {
    const locales = context.locales?.filter((val) => val !== "default") || []
    const paths = await getAllTripPaths(locales)
    return {
        paths,
        fallback: false
    }
}

export const getStaticProps: GetStaticProps = wrapper.getStaticProps(store => async ({params, locale}) => {
    if (locale === "default") {
        return {notFound: true};
    }

    const markdownData = await getTripData(params?.countrySlug as string, params?.tripSlug as string, locale)

    const tripId = markdownData.subDir;

    await store.dispatch(setSelectedTripId(tripId))
    if (getConfig().serverRuntimeConfig.prefetchData) {
        await store.dispatch(fetchReviewsByTag(tripId))
        await store.dispatch(fetchInterestedDivers([tripId]))
    }

    const tripImageNames = fs.readdirSync(getTripsPath(tripId, "img")).filter((filename) => filename.indexOf("__.") < 0)

    const {offerLowUSD, offerHighUSD} = tripDatabase[tripId as keyof typeof tripDatabase]

    const visaReqSource = await getVisaReqSourceForCountrySlug(params?.countrySlug as string, locale || "en")

    return {
        props: {
            tripId,
            offerLowUSD,
            offerHighUSD,
            markdownData,
            tripImageNames,
            visaReqSource,
            ...(await serverSideTranslations(locale as string, ["frontend"])),
        }
    };
})
